import { Country, TvDashboard } from 'src/api/zrm';

import type { TFunction } from 'i18next';

export const PAIRING_CODE_LENGTH = 9;
/** zrm `services/tv/pairing.py` keeps a pending pairing for 10 minutes. */
export const PAIRING_TTL_MS = 10 * 60_000;

const LOOK_ALIKES: Record<string, string> = { O: '0', I: '1', L: '1' };

/** Same normalisation as the backend: upper-case, drop separators, `O→0`, `I/L→1`. */
export const normalizePairingCode = (input: string): string => input
  .toUpperCase()
  .replace(/[^0-9A-Z]/g, '')
  .replace(/[OIL]/g, (c) => LOOK_ALIKES[c]);

/** `7KQM3X9HD` → `7KQ-M3X-9HD`; partial input is grouped as it is typed. */
export const formatPairingCode = (input: string): string => normalizePairingCode(input)
  .slice(0, PAIRING_CODE_LENGTH)
  .match(/.{1,3}/g)
  ?.join('-') ?? '';

export const isCompletePairingCode = (input: string): boolean => normalizePairingCode(input).length === PAIRING_CODE_LENGTH;

/** Each wall screen's path; also the list of links on /tv-screens. */
export const DASHBOARD_PATHS: Record<string, TvDashboard> = {
  '/mb-dash': TvDashboard.Mb,
  '/kh-dash': TvDashboard.Kh,
  '/calls-map': TvDashboard.CallsMap,
  '/web-traffic': TvDashboard.WebTraffic,
  '/live-map': TvDashboard.LiveMap,
};

/*
 * A screen's name and what it shows, translated. `npm run save-translations` only finds keys written as literals
 * inside `t('…')`, so each is spelled out here.
 */
export const dashboardLabel = (t: TFunction, dashboard: TvDashboard): string => {
  switch (dashboard) {
    case TvDashboard.Mb:
      return t('MB dashboard');
    case TvDashboard.Kh:
      return t('KH dashboard');
    case TvDashboard.CallsMap:
      return t('Calls map');
    case TvDashboard.WebTraffic:
      return t('Web traffic');
    case TvDashboard.LiveMap:
      return t('Live map');
  }
};

/** One line on /tv-screens, so an agent picks the right screen. */
export const dashboardDescription = (t: TFunction, dashboard: TvDashboard): string => {
  switch (dashboard) {
    case TvDashboard.Mb:
      return t('MB team: applications per seller and the month\'s goals.');
    case TvDashboard.Kh:
      return t('KH team: sent, helped, TF and LF per credit handler.');
    case TvDashboard.CallsMap:
      return t('Every phone call as it starts, from the office to the customer\'s region.');
    case TvDashboard.WebTraffic:
      return t('Form visitors who identify with BankID, and submitted forms, by town.');
    case TvDashboard.LiveMap:
      return t('Calls and web visitors together on one map.');
  }
};

/** Untranslated; pass through `t`. */
export const COUNTRY_LABELS: Record<Country, string> = {
  [Country.SE]: 'Sweden',
  [Country.NO]: 'Norway',
};

/** The TV dashboard a path belongs to, or null for every agent-app path. */
export const tvDashboardFromPath = (pathname: string): TvDashboard | null => DASHBOARD_PATHS[pathname.replace(/\/$/, '')] ?? null;

export const formatCountdown = (remainingMs: number): string => {
  const seconds = Math.max(0, Math.ceil(remainingMs / 1000));

  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
};

/**
 * When a code just handed out by `/tv/pair/start` runs out, on the screen's own clock — only drives
 * the countdown; the server's 410 on poll is what actually ends a pairing. A fresh code always has
 * close to the full TTL left, so an expiry that looks past, too near or too far means the TV clock
 * is off, and the full TTL is the better guess.
 */
export const pairingDeadline = (expiresAt: string, now: number): number => {
  const remaining = Date.parse(expiresAt) - now;

  return remaining > PAIRING_TTL_MS / 2 && remaining <= PAIRING_TTL_MS ? now + remaining : now + PAIRING_TTL_MS;
};
